import { useTheme } from "@emotion/react";
import Fade from "@mui/material/Fade";
import { useToggle } from "@react-hookz/web";
import React from "react";
import { SwitchTransition } from "react-transition-group";
import { useSdkContext } from "scmp-sdk/components/contexts";
import { useNewsletterContext } from "scmp-sdk/components/newsletter/newsletter-widget/contexts";
import type { ThemeType } from "scmp-sdk/components/newsletter/newsletter-widget/types";

import { notEmpty } from "shared/lib/utils";

import { getNewsletterTitle } from "./helpers";
import { useAction } from "./hooks";
import IconCheckboxCheckedBlack from "./icon-checkbox-checked-black.svg";
import IconCheckboxChecked from "./icon-checkbox-checked.svg";
import IconCheckboxCheckedOff from "./icon-checkbox-off.svg";
import {
  ActionContainer,
  AgreementCheckboxContainer,
  AgreementContainer,
  AgreementDescription,
  Container,
  Description,
  DescriptionContainer,
  Summary,
  SummaryContainer,
  SummaryLine,
  TermAndConditionContainer,
  TermAndConditionLink,
  Title,
  TitleContainer,
} from "./styles";

export type Props = {
  isDisableOpenLinkInSamePage?: boolean;
  isDisableSignupButtonMouseOver?: boolean;
  isShowOverlaySignUp?: boolean;
  onSuccess: () => void;
};

export const SignUpNewsletter: React.FunctionComponent<Props> = ({
  isDisableSignupButtonMouseOver = false,
  isShowOverlaySignUp = false,
  onSuccess,
  ...props
}) => {
  const { handleRoutePush, handleTrackGA4, user } = useSdkContext();
  const isLoggedIn = notEmpty(user?.useremail);
  const theme = useTheme() as unknown as ThemeType;
  const [isAgreedReceiveMarketing, setIsAgreedReceiveMarketing] = useToggle(false);
  const { campaignName, newsletterItemForDisplay, summaryComponent, titleComponent } =
    useNewsletterContext();

  const { actionComponent, handleRenderActionComponent } = useAction({
    isAgreedReceiveMarketing,
    isDisableSignupButtonMouseOver,
    isShowOverlaySignUp,
    onSuccess,
  });

  const renderCheckbox = () => {
    if (isAgreedReceiveMarketing) {
      return theme?.signUpNewsletter?.checkboxStyle === "black" ? (
        <IconCheckboxCheckedBlack />
      ) : (
        <IconCheckboxChecked />
      );
    }
    return <IconCheckboxCheckedOff />;
  };

  if (!newsletterItemForDisplay) return null;

  const computedNewsletterTitle = getNewsletterTitle(newsletterItemForDisplay);

  return (
    <Container {...props}>
      {theme?.hasSummaryLine && <SummaryLine />}
      {theme?.signUpNewsletter?.hasSummary && (
        <SummaryContainer>
          <Summary>{summaryComponent ?? newsletterItemForDisplay?.summary?.text}</Summary>
        </SummaryContainer>
      )}
      {theme?.hasTitle && computedNewsletterTitle && (
        <TitleContainer>
          <Title>{titleComponent ?? computedNewsletterTitle}</Title>
        </TitleContainer>
      )}
      {theme?.signUpNewsletter?.hasDescription && (
        <DescriptionContainer>
          <Description>{newsletterItemForDisplay?.description?.text}</Description>
        </DescriptionContainer>
      )}
      {!isLoggedIn && (
        <AgreementContainer>
          <AgreementDescription>
            {`By submitting, you consent to receiving marketing emails from SCMP. If you don't want these, tick here`}
          </AgreementDescription>
          <AgreementCheckboxContainer onClick={setIsAgreedReceiveMarketing}>
            {renderCheckbox()}
          </AgreementCheckboxContainer>
        </AgreementContainer>
      )}
      <SwitchTransition mode="out-in">
        <Fade key={actionComponent} timeout={500}>
          <ActionContainer>{handleRenderActionComponent()}</ActionContainer>
        </Fade>
      </SwitchTransition>
      <TermAndConditionContainer>
        By registering, you agree to our
        <TermAndConditionLink
          href="/terms-conditions"
          onClick={event => {
            event.preventDefault();
            handleTrackGA4({
              action: "click",
              category: "promopopup",
              customized_parameters: {
                cta_type: "nl_signup",
                destination_url: "/terms-conditions",
                internal_campaign: campaignName ?? "",
                message: "T&C",
                step: "1",
              },
              subcategory: "",
            });
            void handleRoutePush("/terms-conditions");
          }}
        >
          T&C
        </TermAndConditionLink>
        and
        <TermAndConditionLink
          href="/privacy-policy"
          onClick={event => {
            event.preventDefault();
            handleTrackGA4({
              action: "click",
              category: "promopopup",
              customized_parameters: {
                cta_type: "nl_signup",
                destination_url: "/privacy-policy",
                internal_campaign: campaignName ?? "",
                message: "Privacy Policy",
                step: "1",
              },
              subcategory: "",
            });
            void handleRoutePush("/privacy-policy");
          }}
        >
          Privacy Policy
        </TermAndConditionLink>
      </TermAndConditionContainer>
    </Container>
  );
};

SignUpNewsletter.displayName = "SignUpNewsletter";
